import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Add, LocalShipping } from '@mui/icons-material';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs,
  Timestamp 
} from '../../services/firestoreCompat';
import { supabase } from '../../services/supabaseClient';

const AddDriverPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '+49',
    vehicleType: 'bicycle',
    licensePlate: '',
    currentLocation: '',
    role: 'rider',
  });

  const handleInputChange = (field: string) => (e: any) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError('Email and Password are required');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.firstName || !formData.lastName) {
      setError('First name and Last name are required');
      return false;
    }
    return true;
  };

  const handleCreateDriver = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Quick client-side validation so the operator sees actionable errors
      // before we go to the server.
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
      if (!emailOk) {
        setError('Please enter a valid email address.');
        setLoading(false);
        return;
      }

      // All three writes (auth user + staff_users row + drivers row) happen
      // server-side inside the `create-driver` Supabase Edge Function. It
      // verifies the caller is a tenant admin, then uses service-role to
      // create the auth user — something the client can never do.
      console.log('🛰️ Invoking create-driver edge function...');
      const { data: result, error: fnErr } = await supabase.functions.invoke('create-driver', {
        body: {
          email:         formData.email.trim(),
          password:      formData.password,
          name:          `${formData.firstName} ${formData.lastName}`.trim(),
          phone:         formData.phone.trim(),
          vehicle_type:  formData.vehicleType,
          license_plate: formData.licensePlate,
        },
      });

      if (fnErr || (result && result.error)) {
        // Pull the real reason out of the Response body (supabase-js wraps
        // 4xx into FunctionsHttpError; .context is the raw Response).
        let msg = result?.error || fnErr?.message || 'Edge function failed';
        try {
          const ctx: any = (fnErr as any)?.context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) msg = body.error;
          }
        } catch { /* keep whatever we already had */ }
        if (typeof msg === 'string' && msg.toLowerCase().includes('already')) {
          setError('A user with this email already exists. Use Edit to update them instead.');
        } else {
          setError(`Failed to create driver: ${msg}`);
        }
        setLoading(false);
        return;
      }

      console.log('✅ Driver created — uid:', result?.uid, 'driver_id:', result?.driver_id);
      setSuccess(`Driver created successfully! Email: ${formData.email}, Password: ${formData.password}`);

      // Reset form
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phone: '+966',
        vehicleType: 'bicycle',
        licensePlate: '',
        currentLocation: '',
        role: 'rider',
      });
    } catch (error: any) {
      console.error('❌ Error creating driver:', error);
      setError(`Failed to create driver: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 1.5, md: 4 } }}>
        <Typography 
          variant="h3" 
          sx={{ 
            color: '#e0e0e0', 
            fontWeight: 700,
            fontFamily: '"Poppins", sans-serif',
            mb: 4
          }}
        >
          {t('drivers.addNewDriver')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#1e2139', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                <strong>📋 SAVE THESE CREDENTIALS:</strong>
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
                Email: <strong>{formData.email}</strong>
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                Password: <strong>{formData.password}</strong>
              </Typography>
            </Box>
          </Alert>
        )}

        <Paper sx={{ p: { xs: 1.5, md: 4 }, pt: 5 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('common.email')}
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
                sx={{ mb: 3 }}
                InputLabelProps={{
                  style: { marginBottom: '8px', fontSize: '14px' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('common.password')}
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                required
                helperText={t('drivers.passwordMinChars')}
                sx={{ mb: 3 }}
                InputLabelProps={{
                  style: { marginBottom: '8px', fontSize: '14px' }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('drivers.confirmPassword')}
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                required
                helperText={t('drivers.reenterPassword')}
                sx={{ mb: 3 }}
                InputLabelProps={{
                  style: { marginBottom: '8px', fontSize: '14px' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('drivers.firstName')}
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                required
                sx={{ mb: 3 }}
                InputLabelProps={{
                  style: { marginBottom: '8px', fontSize: '14px' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('drivers.lastName')}
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                required
                sx={{ mb: 3 }}
                InputLabelProps={{
                  style: { marginBottom: '8px', fontSize: '14px' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('common.phone')}
                value={formData.phone}
                onChange={handleInputChange('phone')}
                sx={{ mb: 3 }}
                InputLabelProps={{
                  style: { marginBottom: '8px', fontSize: '14px' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel sx={{ fontSize: '14px' }}>{t('drivers.vehicleType')}</InputLabel>
                <Select
                  value={formData.vehicleType}
                  onChange={handleInputChange('vehicleType')}
                  label={t('drivers.vehicleType')}
                >
                  <MenuItem value="bicycle">{t('drivers.bicycle')}</MenuItem>
                  <MenuItem value="motorcycle">{t('drivers.motorcycle')}</MenuItem>
                  <MenuItem value="car">{t('drivers.car')}</MenuItem>
                  <MenuItem value="walking">{t('drivers.walking')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('drivers.licensePlate')}
                value={formData.licensePlate}
                onChange={handleInputChange('licensePlate')}
                sx={{ mb: 3 }}
                InputLabelProps={{
                  style: { marginBottom: '8px', fontSize: '14px' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('drivers.currentLocation')}
                value={formData.currentLocation}
                onChange={handleInputChange('currentLocation')}
                sx={{ mb: 3 }}
                placeholder="e.g. City Center, Munich"
                InputLabelProps={{
                  style: { marginBottom: '8px', fontSize: '14px' }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LocalShipping />}
                onClick={handleCreateDriver}
                disabled={loading}
                fullWidth
                size="large"
                sx={{
                  backgroundColor: '#00C896',
                  color: '#1a1d2e',
                  '&:hover': { backgroundColor: '#7bc912' },
                  py: 1.5,
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 600,
                }}
              >
                {loading ? t('drivers.creatingDriver') : t('drivers.createDriverAccount')}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </AdminLayout>
  );
};

export default AddDriverPage;

